import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { createApplication } from '../../../actions';
import Modal, { ModalBody, ModalFooter } from '../ui/Modal';
import Button from '../ui/Button';
import { TextField } from '../ui/FormField';
import { PlusIcon, TrashIcon } from '../ui/icons';

// Redesign of newadmin/ApplicationAddModal.js — dispatches the same
// createApplication({ name, applicants }) thunk with the same payload shape.
const EMPTY_APPLICANT = { name: '', mobile: '', email: '' };

const NewApplicationModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const [name, setName] = useState('');
    const [applicants, setApplicants] = useState([{ ...EMPTY_APPLICANT }]);

    const reset = () => {
        setName('');
        setApplicants([{ ...EMPTY_APPLICANT }]);
    };

    const close = () => {
        reset();
        onClose();
    };

    const formSubmit = (e) => {
        e.preventDefault();
        dispatch(createApplication({ name, applicants }));
        close();
    };

    const applicantOnChange = (field, value, key) => {
        const next = [...applicants];
        next[key] = { ...next[key], [field]: value };
        setApplicants(next);
    };

    const addApplicant = () => {
        setApplicants([...applicants, { ...EMPTY_APPLICANT }]);
    };

    const deleteApplicant = (key) => {
        if (applicants.length > 1) {
            setApplicants(applicants.filter((_, i) => i !== key));
        }
    };

    return (
        <Modal
            open={open}
            onClose={close}
            title="New Application"
            subtitle="Create an application and its clients."
            size="lg"
        >
            <form onSubmit={formSubmit} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <ModalBody className="flex-1 space-y-5">
                    <TextField
                        label="Application Name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Smith Home Loan"
                        hint="A descriptive name for the application."
                    />

                    <div className="space-y-4">
                        {applicants.map((applicant, key) => (
                            <div key={key} className="rounded-2xl border border-[#E5E7EB] bg-[#FAFAFC] p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-bold uppercase tracking-wide text-[#6B7280]">
                                        Client {key + 1}
                                    </p>
                                    {applicants.length > 1 ? (
                                        <button
                                            type="button"
                                            onClick={() => deleteApplicant(key)}
                                            aria-label={`Remove client ${key + 1}`}
                                            className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-[#DC2626] hover:bg-[#FEF2F2] transition-colors"
                                        >
                                            <TrashIcon className="h-3.5 w-3.5" />
                                            Remove
                                        </button>
                                    ) : null}
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <TextField
                                        label="Client Name"
                                        required
                                        value={applicant.name}
                                        onChange={(e) => applicantOnChange('name', e.target.value, key)}
                                        placeholder="Client Name"
                                    />
                                    <TextField
                                        label="Client Mobile"
                                        required
                                        value={applicant.mobile}
                                        onChange={(e) => applicantOnChange('mobile', e.target.value, key)}
                                        placeholder="Client Mobile"
                                    />
                                    <TextField
                                        label="Client Email"
                                        type="email"
                                        value={applicant.email}
                                        onChange={(e) => applicantOnChange('email', e.target.value, key)}
                                        placeholder="Client Email"
                                        className="sm:col-span-2"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={addApplicant}
                        className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#D1D5DB] py-3 text-sm font-semibold text-[#6B7280] hover:border-[#7C5CFC] hover:text-[#7C5CFC] transition-colors"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add Another Client
                    </button>
                </ModalBody>
                <ModalFooter>
                    <Button variant="secondary" onClick={close}>
                        Close
                    </Button>
                    <Button type="submit">Create Application</Button>
                </ModalFooter>
            </form>
        </Modal>
    );
};

export default NewApplicationModal;
